import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Button } from '../components/ui/button'
import { Panel } from '../components/ui/panel'
import { Chip } from '../components/ui/tone'
import { api } from '../lib/api'

/**
 * Observability (Phase 8, item 51).
 *
 * Real-time visibility into system state: scheduler, queue, recent activity, errors.
 * Structured JSON logging with correlation fields (P052).
 */

interface SchedulerStatus {
  enabled: boolean
  lastTick?: string
  nextTick?: string
  ticksRun: number
  errors: number
}

interface QueueStatus {
  driver: 'timer' | 'redis'
  connected: boolean
  // Null under the timer driver — there is no queue to be deep. Rendered as "no queue" rather
  // than as 0, which would read as "nothing waiting" (P054).
  jobsQueued: number | null
  workersActive: number | null
}

interface RecentMessage {
  id: string
  campaignId: string
  recipient: string
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'SENT' | 'FAILED'
  sentAt?: string
  error?: string
}

interface ObservabilityData {
  scheduler: SchedulerStatus
  queue: QueueStatus
  recentMessages: RecentMessage[]
  systemHealth: {
    uptime: number
    memoryUsage: number
    databaseLatency: number
  }
}

export function ObservabilityScreen() {
  const [autoRefresh, setAutoRefresh] = useState(true)

  // Poll every 5 seconds if auto-refresh is on
  const observability = useQuery({
    queryKey: ['observability'],
    queryFn: () => api.get<ObservabilityData>('/system/observability'),
    refetchInterval: autoRefresh ? 5000 : false,
    retry: false,
  })

  const manualTick = useMutation({
    mutationFn: () => api.post('/sending/tick', {}),
    onSuccess: () => {
      void observability.refetch()
    },
  })

  const data = observability.data

  return (
    <Screen>
      <ScreenHeader
        title="Observability"
        description="System state, scheduler, queue and recent activity"
      />

      <div className="space-y-4">
        {/* Scheduler Status */}
        <Panel title="Scheduler" description="§22 sweep status">
          {data ? (
            <div className="space-y-2 px-4 py-3 text-[13px]">
              <div className="flex items-center justify-between">
                <span className="text-muted">Status</span>
                <Chip tone={data.scheduler.enabled ? 'ok' : 'warn'}>
                  {data.scheduler.enabled ? 'Enabled' : 'Disabled'}
                </Chip>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted">Ticks run</span>
                <span className="tabular-nums">{data.scheduler.ticksRun}</span>
              </div>
              {data.scheduler.errors > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-muted">Errors</span>
                  <span className="tabular-nums text-bad">{data.scheduler.errors}</span>
                </div>
              )}
              {data.scheduler.lastTick && (
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-muted">Last tick</span>
                  <span className="tabular-nums text-faint">
                    {new Date(data.scheduler.lastTick).toLocaleTimeString()}
                  </span>
                </div>
              )}
              <Button
                size="sm"
                className="mt-2"
                onClick={() => manualTick.mutate()}
                disabled={manualTick.isPending}
              >
                {manualTick.isPending ? 'Running…' : 'Run sweep now'}
              </Button>
            </div>
          ) : (
            <div className="px-4 py-3 text-muted text-[12.5px]">Loading…</div>
          )}
        </Panel>

        {/* Queue Status */}
        <Panel title="Queue" description={data ? `Driver: ${data.queue.driver}` : 'Loading…'}>
          {data ? (
            <div className="space-y-2 px-4 py-3 text-[13px]">
              <div className="flex items-center justify-between">
                <span className="text-muted">Driver</span>
                <Chip tone={data.queue.driver === 'timer' ? 'ok' : 'info'}>
                  {data.queue.driver === 'timer' ? 'Timer (in-process)' : 'Redis'}
                </Chip>
              </div>
              {data.queue.driver === 'redis' && (
                <div className="flex items-center justify-between">
                  <span className="text-muted">Connected</span>
                  <Chip tone={data.queue.connected ? 'ok' : 'bad'}>
                    {data.queue.connected ? 'Connected' : 'Disconnected'}
                  </Chip>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-muted">Queued jobs</span>
                {data.queue.jobsQueued === null ? (
                  <span className="text-muted text-[12.5px]">
                    no queue — the sweep runs on a timer here
                  </span>
                ) : (
                  <span className="tabular-nums">{data.queue.jobsQueued}</span>
                )}
              </div>
              {data.queue.workersActive !== null && data.queue.workersActive > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-muted">Active workers</span>
                  <span className="tabular-nums">{data.queue.workersActive}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="px-4 py-3 text-muted text-[12.5px]">Loading…</div>
          )}
        </Panel>

        {/* System Health */}
        <Panel title="System health" description="Resource usage and latency">
          {data ? (
            <div className="space-y-2 px-4 py-3 text-[13px]">
              <div className="flex items-center justify-between">
                <span className="text-muted">Uptime</span>
                <span className="tabular-nums">
                  {Math.floor(data.systemHealth.uptime / 3600)}h{' '}
                  {Math.floor((data.systemHealth.uptime % 3600) / 60)}m
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted">Memory</span>
                <span className="tabular-nums">
                  {Math.round(data.systemHealth.memoryUsage)}%
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted">DB latency (p99)</span>
                <span className="tabular-nums">{Math.round(data.systemHealth.databaseLatency)}ms</span>
              </div>
            </div>
          ) : (
            <div className="px-4 py-3 text-muted text-[12.5px]">Loading…</div>
          )}
        </Panel>

        {/* Recent Activity */}
        <Panel title="Recent messages" description="Last 10 sent or failed">
          {data ? (
            <div className="space-y-1">
              {data.recentMessages.length === 0 ? (
                <div className="px-4 py-3 text-muted text-[12.5px]">No recent messages</div>
              ) : (
                <div className="divide-line2 divide-y">
                  {data.recentMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className="flex items-start justify-between gap-2 px-4 py-2 text-[12px]"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-mono text-muted">{msg.recipient}</div>
                        <div className="mt-0.5 text-faint text-[11px]">
                          {msg.status === 'SENT' && msg.sentAt && (
                            <>
                              Sent {new Date(msg.sentAt).toLocaleTimeString()}
                            </>
                          )}
                          {msg.status === 'FAILED' && msg.error && (
                            <span className="text-bad">{msg.error}</span>
                          )}
                          {msg.status === 'SCHEDULED' && 'Scheduled'}
                          {msg.status === 'IN_PROGRESS' && 'Sending'}
                        </div>
                      </div>
                      <Chip
                        tone={
                          msg.status === 'SENT'
                            ? 'ok'
                            : msg.status === 'FAILED'
                              ? 'bad'
                              : msg.status === 'IN_PROGRESS'
                                ? 'info'
                                : 'neutral'
                        }
                      >
                        {msg.status === 'IN_PROGRESS' ? 'Sending' : msg.status}
                      </Chip>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="px-4 py-3 text-muted text-[12.5px]">Loading…</div>
          )}
        </Panel>

        {/* Auto-refresh toggle */}
        <div className="flex items-center gap-2 px-4">
          <input
            type="checkbox"
            id="autorefresh"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="rounded"
          />
          <label htmlFor="autorefresh" className="text-muted text-[13px] cursor-pointer">
            Auto-refresh every 5 seconds
          </label>
        </div>
      </div>
    </Screen>
  )
}
